// translations/pages/kyveil/sv.js

export const kyveilPageSV = {
  "seo": {
    "title": "Kyveil PII-anonymisering | pAIoneer Labs GmbH",
    "description": "Kyveil identifierar och anonymiserar personuppgifter lokalt på dina system. Skrivbordsprogram eller REST-API för GDPR-anpassad PII-anonymisering.",
    "keywords": "Kyveil, PII-anonymisering, anonymisera personuppgifter, dataskydd, integritet, GDPR, Microsoft Presidio, spaCy, flair, lokal databehandling",
    "ogTitle": "Kyveil: professionell PII-anonymisering med AI och NLP",
    "ogDescription": "Automatisk identifiering och anonymisering av personuppgifter som skrivbordsprogram eller REST-API i din egen infrastruktur.",
    "twitterTitle": "Kyveil PII-anonymisering",
    "twitterDescription": "Lokal identifiering och anonymisering av personuppgifter i känsliga dokument.",
    "url": "https://paioneer-labs.eu/kyveil",
    "structuredData": {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Kyveil",
      "applicationCategory": "SecurityApplication",
      "operatingSystem": "Windows, macOS, Linux, Server",
      "description": "Programvara för lokal identifiering och anonymisering av personuppgifter med AI och NLP.",
      "provider": {
        "@type": "Organization",
        "name": "pAIoneer Labs GmbH",
        "url": "https://paioneer-labs.eu"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "url": "https://paioneer-labs.eu/kyveil"
      }
    }
  },
  "hero": {
    "eyebrow": "Dataskydd och anonymisering",
    "title": "Kyveil",
    "subtitle": "Lokal anonymisering före AI, tester och delning.",
    "description": "Kyveil är ett lokalt anonymiseringsskikt för dokument och AI-arbetsflöden. Programvaran identifierar personuppgifter i Word-, Excel-, PDF- och textfiler, ersätter eller maskerar dem och visar en förhandsgranskning innan innehållet går vidare till AI-chattar, tester, analyser eller extern delning. Kyveil utvecklas och vidareutvecklas kontinuerligt [IDD-drivet](idd) (Intent-Driven Development) och automatiserat med [Veridion](veridion) av våra AI-medarbetare med människor i loopen.",
    "platforms": "Skrivbordsprogram, REST-API och AnonChat",
    "proofPoints": [
      "Dokumenten stannar lokalt på dina system.",
      "Personuppgifter ersätts eller maskeras.",
      "En förhandsvisning visar det anonymiserade innehållet före delning eller AI-chatt.",
      "Word, Excel, PDF och text är de centrala dokumentflödena."
    ]
  },
  "dataSovereignty": {
    "title": "Dina data. Dina system. Din kontroll.",
    "subtitle": "Fullständig datasuveränitet genom lokal bearbetning.",
    "items": [
      {
        "title": "Inget moln behövs",
        "description": "All bearbetning sker lokalt i din infrastruktur. Dina filer lämnar aldrig dina system. Inga molnberoenden, ingen extern dataöverföring."
      },
      {
        "title": "Edge computing",
        "description": "Skrivbordsprogrammet körs helt fristående på arbetsstationer. API:et driftsätts på dina egna servrar. Båda varianterna bearbetar alla data lokalt inom din säkerhetszon."
      },
      {
        "title": "Dataautonomi",
        "description": "Full kontroll över dina dataflöden. Ingen leverantörsinlåsning. Inga löpande molnkostnader. Din känsliga information förblir uteslutande under din kontroll."
      },
      {
        "title": "Standardhårdvara",
        "description": "Ingen GPU krävs. Körs på vanliga kontorsdatorer och standardservrar. Inga investeringar i specialiserad hårdvara behövs."
      }
    ]
  },
  "features": {
    "title": "Nyckelfunktioner",
    "subtitle": "Allt som behövs för kontrollerad identifiering och anonymisering av personuppgifter i företagets processer.",
    "items": [
      {
        "title": "AI-baserad PII-identifiering",
        "description": "Automatisk identifiering med Microsoft Presidio SDK, spaCy från Explosion AI och flair från Humboldt-Universität zu Berlin.",
        "tone": "primary"
      },
      {
        "title": "Replace-läge",
        "description": "Ersätter identifierade personuppgifter med generiska platshållare som [NAME], [EMAIL] och [ADDRESS] för scenarier med krav på regelefterlevnad.",
        "tone": "info"
      },
      {
        "title": "Fake-läge",
        "description": "Ersätter identifierade personuppgifter med plausibla ersättningsvärden för testmiljöer och utveckling.",
        "tone": "warning"
      },
      {
        "title": "Flera dokumentformat",
        "description": "Bearbetar Microsoft Word, Excel, OpenDocument, PDF-filer och ren text.",
        "tone": "success"
      },
      {
        "title": "Flerspråkigt stöd",
        "description": "Inbyggt stöd för tyska, engelska, franska, italienska och spanska med språkspecifika NLP-modeller.",
        "tone": "secondary"
      },
      {
        "title": "Konfigurerbar identifiering",
        "description": "Ange vilka PII-entiteter som ska identifieras: namn, adresser, e-postadresser, telefonnummer, bankkonton, skattenummer med mera.",
        "tone": "primary"
      },
      {
        "title": "Utbyggbarhet",
        "description": "Gränssnitt för egen för- och efterbearbetningslogik, så att lösningen kan integreras i dina specifika arbetsflöden.",
        "tone": "info"
      },
      {
        "title": "CPU-optimerad",
        "description": "Den effektiva modellarkitekturen körs på vanlig kontorshårdvara utan krav på GPU.",
        "tone": "warning"
      }
    ]
  },
  "technology": {
    "title": "Teknikstack",
    "subtitle": "Ramverk i företagsklass och beprövad NLP-forskning.",
    "items": [
      {
        "title": "Microsoft Presidio SDK",
        "description": "Microsoft Data Protection and De-identification SDK. Branschstandard för identifiering och anonymisering av personuppgifter, med inbyggda entitetsigenkännare.",
        "tone": "primary"
      },
      {
        "title": "spaCy",
        "subtitle": "Utvecklat av Explosion AI",
        "description": "Industrial-Strength Natural Language Processing. Produktionsklart NLP-bibliotek med förtränade statistiska modeller för igenkänning av namngivna entiteter.",
        "tone": "info"
      },
      {
        "title": "flair",
        "subtitle": "Utvecklat vid Humboldt-Universität zu Berlin",
        "description": "Toppmodernt NLP-ramverk med kontextuella stränginbäddningar. Avancerade neurala nätverksmodeller för högre träffsäkerhet vid entitetsidentifiering.",
        "tone": "secondary"
      }
    ],
    "integration": "Integrerad AI-pipeline",
    "integrationDesc": "Kombinerad identifiering med Presidio som orkestreringslager tillsammans med spaCy- och flair-modeller för bästa möjliga resultat över flera språk och entitetstyper."
  },
  "technicalDetails": {
    "title": "Tekniska möjligheter",
    "subtitle": "Konfigurerbar identifiering för europeiska affärsdokument.",
    "items": [
      {
        "title": "Språkstöd",
        "description": "Förtränade NLP-modeller för tyska, engelska, franska, italienska och spanska. Språkspecifik entitetsigenkänning för träffsäker PII-identifiering i europeiska affärssammanhang.",
        "tone": "primary"
      },
      {
        "title": "Konfigurerbara PII-entiteter",
        "description": "Välj vilka typer av personuppgifter som ska identifieras och anonymiseras: personnamn, e-postadresser, telefonnummer, postadresser, IBAN-bankkonton, kreditkortsnummer, socialförsäkringsnummer, skattenummer, passnummer med mera.",
        "tone": "info"
      },
      {
        "title": "Anonymiseringslägen",
        "description": "Replace-läget ersätter personuppgifter med generiska platshållare för dokumentation av regelefterlevnad. Fake-läget använder plausibla ersättningsvärden för testmiljöer och bevarar samtidigt dokumentstrukturen.",
        "tone": "warning"
      },
      {
        "title": "För- och efterbearbetning",
        "description": "Implementera egen logik före och efter anonymiseringen. Lägg till dokumentvalidering, formatkonvertering, kvalitetskontroller, granskningsloggning eller integration med efterföljande system via de gränssnitt som tillhandahålls.",
        "tone": "secondary"
      }
    ]
  },
  "anonChat": {
    "title": "AnonChat utökar Kyveil med en AI-webbchatt",
    "subtitle": "Den nya appen använder samma anonymiseringsmetod före den egentliga chatten med externa eller interna AI-system.",
    "eyebrow": "Ny applikation",
    "name": "AnonChat",
    "description": "AnonChat är en AI-webbchatt där Word- och Excel-dokument kan anonymiseras innan de används med ChatGPT, Claude, Gemini eller interna LLM:er. En förhandsvisning visar den anonymiserade texten innan innehållet förs över till chatten.",
    "statement": "Beslutet ligger därmed hos användaren: granska, godkänna och först därefter chatta.",
    "steps": [
      {
        "title": "Ladda dokument",
        "description": "Word- eller Excel-filer förbereds för anonymisering innan de går in i AI-arbetsflödet.",
        "tone": "primary"
      },
      {
        "title": "Anonymisera personuppgifter",
        "description": "Kyveil identifierar personuppgifter och ersätter eller maskerar dem enligt de valda reglerna.",
        "tone": "info"
      },
      {
        "title": "Granska förhandsvisningen",
        "description": "Den anonymiserade texten visas innan innehållet lämnas över till chatten.",
        "tone": "warning"
      },
      {
        "title": "Använd AI-chatten",
        "description": "Först efter granskningen används det anonymiserade innehållet i chatten med externa eller interna LLM:er.",
        "tone": "success"
      }
    ]
  },
  "deployment": {
    "title": "Driftsättningsalternativ",
    "subtitle": "Välj den lösning som passar ditt användningsfall.",
    "options": [
      {
        "title": "Skrivbordsprogram",
        "subtitle": "Fristående program för enskilda arbetsplatser",
        "description": "Skrivbordsversionen körs helt fristående på Windows, macOS eller Linux. Idealisk för användare som behöver anonymisera dokument direkt på arbetsplatsen utan nätverksberoenden.",
        "features": [
          "100 % offline - ingen internetanslutning krävs",
          "Filerna lämnar aldrig den lokala arbetsstationen",
          "Batchbearbetning av flera filer",
          "Grafiskt användargränssnitt för enkel hantering",
          "Körs på vanliga kontorsdatorer utan GPU",
          "Engångslicens - ingen prenumerationsmodell"
        ]
      },
      {
        "title": "REST-API",
        "subtitle": "Serverdriftsättning för integration och skalning",
        "description": "API-versionen driftsätts på dina egna servrar eller i en privat molninfrastruktur. Idealisk för att integrera anonymisering i befintliga system och arbetsflöden med stora bearbetningsvolymer.",
        "features": [
          "Driftsättning i egen infrastruktur - on-premises eller privat moln",
          "Horisontell skalning för höga krav på genomströmning",
          "RESTful API med utförlig dokumentation",
          "Samma anonymiseringsmotor som i skrivbordsversionen",
          "Integrationsexempel för vanliga plattformar",
          "Stöd för anpassad för- och efterbearbetning"
        ]
      },
      {
        "title": "AnonChat",
        "subtitle": "AI-webbchatt med anonymisering före chatten",
        "description": "AnonChat placerar Kyveil framför den egentliga AI-chatten. Word- och Excel-dokument anonymiseras, en förhandsvisning granskas och först därefter används innehållet med ChatGPT, Claude, Gemini eller interna LLM:er.",
        "features": [
          "Anonymisering före chatten i stället för kontroll i efterhand",
          "Förhandsvisning av den anonymiserade texten före överlämningen",
          "Utformad för dokumentbaserade frågor till AI-system",
          "Minskar risken att personuppgifter skickas till externa modeller",
          "Kan anslutas till interna LLM-miljöer eller företagets chattlösningar",
          "Bygger på samma anonymiseringsmetod som skrivbordsprogrammet och REST-API:et"
        ]
      }
    ],
    "bothNote": "Skrivbordsprogrammet, REST-API:et och AnonChat är utformade för att anonymisera känsligt innehåll på ett kontrollerat sätt före vidare bearbetning."
  },
  "useCases": {
    "title": "Användningsfall",
    "subtitle": "Från GDPR-efterlevnad till säkra utvecklingsdata.",
    "items": [
      {
        "title": "GDPR-efterlevnad",
        "description": "Anonymisera personuppgifter innan dokument delas externt eller med interna avdelningar. Uppfyll dataskyddskraven för testdata, analysdatamängder och dokumentation.",
        "tone": "primary"
      },
      {
        "title": "Förberedelse av testdata",
        "description": "Förbered realistiska testdatamängder så att utvecklare kan arbeta med produktionsnära data utan att verkliga personuppgifter röjs.",
        "tone": "info"
      },
      {
        "title": "Bearbetning av HR-dokument",
        "description": "Anonymisera ansökningar, personalakter och HR-dokumentation för opartiska granskningar, efterlevnadsrevisioner eller dataanalyser, samtidigt som den personliga integriteten skyddas.",
        "tone": "warning"
      },
      {
        "title": "Dataanalys",
        "description": "Gör det möjligt att analysera dokument som innehåller personuppgifter genom att först anonymisera känslig information. Dela datamängder mellan team utan dataskyddsbetänkligheter.",
        "tone": "success"
      },
      {
        "title": "Dokumentarkivering",
        "description": "Förbered dokument för långtidsarkivering genom att ta bort eller anonymisera personuppgifter i enlighet med riktlinjer för lagringstid och dataskyddsregler.",
        "tone": "secondary"
      },
      {
        "title": "Säker delning",
        "description": "Dela dokument med externa partner, uppdragstagare eller andra avdelningar efter att känsliga personuppgifter har tagits bort automatiskt.",
        "tone": "error"
      }
    ]
  },
  "releases": {
    "title": "Varianter och support",
    "subtitle": "Uppdateringar, utökningar och support för användning i produktion.",
    "items": [
      {
        "title": "Skrivbordsprogram, API och AnonChat",
        "description": "Kyveil finns som skrivbordsprogram och som REST-API för integrationer. AnonChat är webbchatten med dokumentanonymisering före chatten.",
        "tone": "info"
      },
      {
        "title": "Support",
        "description": "För användning i företag erbjuder vi teknisk support, hjälp med implementeringen och anpassningar.",
        "tone": "success"
      }
    ]
  },
  "cta": {
    "title": "Kom igång med Kyveil",
    "subtitle": "Skydda känsliga dokument innan de används för AI, tester, analyser eller delning.",
    "desktop": "Begär skrivbordsprogrammet",
    "desktopSubject": "Kyveil Desktop",
    "apiContact": "Kontakt för driftsättning av REST-API",
    "contact": "Skriv till oss för en demo eller en offert.",
    "email": "info@paioneer.eu",
    "apiSubject": "Driftsättning av Kyveil REST-API",
    "contactSubject": "Kyveil: demo eller offert"
  }
};
